import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import GuestHomepage from '../components/pages/GuestHomepage';
import GuestHomepageSport from '../components/pages/GuestHomepageSport';

import { NextPageWithLayout } from './page';
import { t } from 'i18next';
import GuestLayout from '../components/layouts/guest';
import { internalFetch } from '../utils';
import { ITopBannerHomeProps } from '../components/pages/GuestHomepage/TopBannerHome';
import * as gtag from '../lib/gtag';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import axios from 'axios';
import { deleteCookie, getCookie } from 'cookies-next';
import { IWelcomePackageStrapi } from 'Components/pages/GuestHomepage/types';

enum ELandingVersions {
  null,
  'b',
  'c',
  'b2',
  'c2',
}
interface ISessionProps {
  welcome_offers?: any;
  payment_methods?: any;
  player_type?: string;
  regtype?: string;
  sport_theme?: any;
  theme: ITopBannerHomeProps;
  version?: number;
  dynamic_welcome_package?: Array<IWelcomePackageStrapi>;
  faq?: any;
  free_spins_block?: any;
  batchName?: string;
  locale: string;
  sport_landing?: any;
  sport_name?: string;
}

const mapTitlesFromStrapiResponse = ({ res, pt, country }) => {
  let titles = res?.data?.data?.attributes?.market_titles?.find(
    ({ countries_list, country_switcher }) => countries_list?.includes(country) && country_switcher,
  )?.titles;

  // country not found, user world
  if (!titles) {
    titles = res?.data?.data?.attributes?.market_titles?.find(({ country_switcher }) => !country_switcher)?.titles;
  }

  switch (pt) {
    case 'casino':
      return titles?.reduce?.(
        (acc, { identifier, title }) => ({
          ...acc,
          [identifier]: title,
        }),
        {},
      );
    default: // any sport
      const sportLanding = res?.data?.sport_landing?.[0];

      if (!sportLanding) {
        return titles?.reduce?.(
          (acc, { identifier, title }) => ({
            ...acc,
            [identifier]: title,
          }),
          {},
        );
      }

      return {
        top_banner_title: sportLanding.top_banner_title,
        top_banner_subtitle: sportLanding.top_banner_subtitle,
      };
  }
};

export const getServerSideProps = async ctx => {
  const { req, locale } = ctx;
  /**pt - player_type */
  const { pt = 'casino', regtype = 'email', country, _appSignature = 'not-set' } = req.cookies;
  const res: any = await internalFetch(
    `/api/content/winnerz?populate=deep&locale=${locale}&version=${4}&geo=${ctx?.req?.cookies?.country}&pt=${pt}`,
  );
  //default ?? sport ?? not found
  const theme =
    res?.data?.data?.attributes?.dynamic_theme ?? res?.data?.dynamic_theme?.data?.attributes?.theme ?? undefined;

  const sport_theme = res?.data?.sport_landing?.[0]?.theme?.data?.attributes?.theme ?? undefined;
  const sport_name = res?.data?.sport_landing?.[0]?.name;

  const titles = mapTitlesFromStrapiResponse({ res, pt, country });

  return {
    props: {
      faq: res?.data?.data?.attributes?.faq ?? [],
      dynamic_welcome_package:
        res?.data?.data?.attributes?.dynamic_welcome_package ?? res?.data?.dynamic_welcome_package ?? [],
      welcome_offers: res?.data?.data?.attributes?.welcome_offers ?? [],
      payment_methods: res?.data?.data?.attributes?.payment_methods ?? res?.data?.payment_methods ?? [],
      player_type: pt,
      sport_name: sport_name ?? null,
      version: 4,
      free_spins_block: res?.data?.data?.attributes?.free_spins_block ?? [],
      sport_theme: {
        fallback_image: {
          url: sport_theme?.fallback_image?.data?.attributes?.url ?? null,
        },
        mobile: {
          mime: sport_theme?.mobile?.data?.attributes?.mime ?? null,
          url: sport_theme?.mobile?.data?.attributes?.url ?? null,
        },
        desktop: {
          mime: sport_theme?.desktop?.data?.attributes?.mime ?? null,
          url: sport_theme?.desktop?.data?.attributes?.url ?? null,
        },
        desktop_xl: {
          mime: sport_theme?.desktop_xl?.data?.attributes?.mime ?? null,
          url: sport_theme?.desktop_xl?.data?.attributes?.url ?? null,
        },
        desktop_xxl: {
          mime: sport_theme?.desktop_xxl?.data?.attributes?.mime ?? null,
          url: sport_theme?.desktop_xxl?.data?.attributes?.url ?? null,
        },
      },
      theme: {
        fallback_image: {
          url: theme?.fallback_image?.data?.attributes?.url ?? null,
        },
        mobile: {
          mime: theme?.mobile?.data?.attributes?.mime ?? null,
          url: theme?.mobile?.data?.attributes?.url ?? null,
        },
        desktop: {
          mime: theme?.desktop?.data?.attributes?.mime ?? null,
          url: theme?.desktop?.data?.attributes?.url ?? null,
        },
        desktop_xl: {
          mime: theme?.desktop_xl?.data?.attributes?.mime ?? null,
          url: theme?.desktop_xl?.data?.attributes?.url ?? null,
        },
        desktop_xxl: {
          mime: theme?.desktop_xxl?.data?.attributes?.mime ?? null,
          url: theme?.desktop_xxl?.data?.attributes?.url ?? null,
        },
        top_banner_line: {
          mime: res?.data?.data?.attributes?.top_banner_line?.data?.attributes?.mime ?? null,
          url: res?.data?.data?.attributes?.top_banner_line?.data?.attributes?.url ?? null,
        },
        top_banner_line_xl: {
          mime: res?.data?.data?.attributes?.top_banner_line_xl?.data?.attributes?.mime ?? null,
          url: res?.data?.data?.attributes?.top_banner_line_xl?.data?.attributes?.url ?? null,
        },
        top_banner_line_xxl: {
          mime: res?.data?.data?.attributes?.top_banner_line_xxl?.data?.attributes?.mime ?? null,
          url: res?.data?.data?.attributes?.top_banner_line_xxl?.data?.attributes?.url ?? null,
        },
      },
      sport_landing: res?.data?.data?.attributes?.sport_landing ?? null,
      regtype,
      batchName: process.env.ACTIVE_LANDING ?? _appSignature,
      locale,
      titles: titles ?? null,
    },
  };
};

function ContentSWR(landingIndex, locale, player_type) {
  const geo = getCookie('country');
  const fetcher = url => fetch(url).then(r => r.json());
  const { data, error } = useSWR(
    `/api/content/winnerz?populate=deep&locale=${locale}&version=${landingIndex}&geo=${geo}&pt=${player_type}`,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  if (error) return <></>;
  return data || {};
}

const GuestHome: NextPageWithLayout = ({
  player_type,
  sport_theme,
  theme,
  sport_name,
  regtype,
  welcome_offers,
  payment_methods,
  version = 4,
  dynamic_welcome_package,
  faq,
  free_spins_block,
  batchName,
}: ISessionProps) => {
  const { locale } = useRouter();
  const response = ContentSWR(version, locale, player_type);
  const geo = getCookie('country');
  const { data, sport_landing } = response;

  const [cmsData, setCmsData] = useState({
    attributes: {
      ...data?.attributes,
      welcome_offers,
      dynamic_welcome_package,
      free_spins_block,
      payment_methods,
    },
    id: data?.id,
  });

  useEffect(() => {
    localStorage?.setItem?.('landing-version', ELandingVersions[version]);
    // delete when entering homepage
    deleteCookie('welcomeBonus');
  }, [version]);

  useEffect(() => {
    const revalidateContent = async () => {
      const res = await axios.get(
        `/api/content/winnerz?populate=deep&locale=${locale}&version=${version}&geo=${geo}&pt=${player_type}`,
      );

      const attributes = player_type === 'sport' ? res?.data : res?.data?.data?.attributes;
      // fingers crossed
      setCmsData(current => ({
        ...current,
        attributes: {
          ...current?.attributes,
          ...attributes,
        },
      }));
    };
    if (cmsData?.attributes && cmsData?.attributes?.locale && cmsData?.attributes?.locale !== locale) {
      revalidateContent();
    }
  }, [locale]);

  useEffect(() => {
    gtag?.setVariables?.({
      BATCH: batchName,
    });

    gtag?.event?.(
      'visit-landing-page',
      {
        category: 'conversion',
        label: 'visit landing page',
      },
      {
        BATCH: batchName,
      },
    );
  }, []);

  if (sport_name) {
    return (
      <div className="container-app">
        <GuestHomepageSport
          sport_landing={sport_landing}
          player_type={player_type}
          theme={sport_theme}
          regtype={regtype}
          payment_methods={cmsData?.attributes?.payment_methods}
          dynamic_welcome_package={cmsData?.attributes?.dynamic_welcome_package ?? dynamic_welcome_package}
        />
      </div>
    );
  }
  return (
    <div className="container-app">
      <GuestHomepage
        theme={theme}
        regtype={regtype}
        payment_methods={cmsData?.attributes?.payment_methods}
        dynamic_welcome_package={cmsData?.attributes?.dynamic_welcome_package ?? dynamic_welcome_package}
        free_spins_block={cmsData?.attributes?.free_spins_block ?? free_spins_block}
      />
    </div>
  );
};

export default GuestHome;

GuestHome.getLayout = page => {
  return (
    <React.Fragment>
      <Head>
        <title>{t('page.home.meta.title')}</title>
        <meta name="description" content={t('page.home.meta.description')}></meta>
      </Head>
      <GuestLayout key={page?.props?.locale} version={page?.props?.version}>
        {page}
      </GuestLayout>
    </React.Fragment>
  );
};
